/**
 * Suggesting a Knowledge Base source name from the thing being added — an
 * uploaded file's name, or the URL being indexed.
 *
 * Only ever a suggestion: the Add Source dialog fills the Name field with this
 * while the user hasn't typed one, and a wrong guess costs a single keystroke to
 * fix. That tolerance is what licenses the rules below to stay simple; anything
 * that needed a dictionary to get right was left out deliberately.
 *
 * The one rule worth internalizing is the casing rule. A token that already
 * contains a capital is left exactly as the author wrote it, and only an
 * all-lowercase token gets capitalized. Blanket Title Case would read fine on
 * `quick-start-guide` and destroy `KEPServerEX`, `ThingWorx` and `PTC` — which
 * is most of what a product knowledge base is actually named after. Nothing here
 * ever lowercases a character the author typed as uppercase.
 *
 * For the same reason CamelCase is never split: `QuickStart` -> "Quick Start"
 * would be nice, but the identical rule turns `ThingWorx` into "Thing Worx".
 *
 * Written up in gaussiq-devs/docs/design/rag.md ("Source name suggestion").
 *
 * @see nameFromFilename
 * @see nameFromUrl
 */

// Left lowercase inside a name, the way a title is normally set. Applied only to
// a token that was already lowercase, so an author's deliberate "The" survives.
const SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or',
  'the', 'to', 'vs', 'with',
])

/**
 * Separators become spaces — except a '.' or '-' with a digit on BOTH sides,
 * which is far more likely to be part of something meaningful than a separator:
 * `25.1`, `v6.11`, `2024-03-15`. One digit-adjacent side isn't enough, so
 * `part-2` still splits.
 */
function separatorsToSpaces(text) {
  return text
    .replace(/%20/gi, ' ')
    .replace(/[_+]+/g, ' ')
    .replace(/[.-]/g, (separator, index, whole) => {
      const before = whole[index - 1]
      const after = whole[index + 1]
      return before && after && /\d/.test(before) && /\d/.test(after) ? separator : ' '
    })
}

function capitalizeToken(token, isFirst) {
  if (/[A-Z]/.test(token)) return token                       // the author's capitalization wins
  // `v6.11`, `v2`. The lowercase v is the convention for a version marker, and
  // capitalizing it reads like the first letter of a word instead.
  if (/^v\d/.test(token)) return token
  if (!isFirst && SMALL_WORDS.has(token)) return token
  return token.charAt(0).toUpperCase() + token.slice(1)
}

function humanize(text) {
  const tokens = separatorsToSpaces(text).split(/\s+/).filter(Boolean)
  return tokens.map((token, index) => capitalizeToken(token, index === 0)).join(' ')
}

/**
 * Cut to the Name field's own limit, on a word boundary so the suggestion never
 * ends mid-word. The cap is passed in rather than imported because it belongs to
 * the field being filled, not to this derivation; omit it to skip the cut.
 */
function truncateAtWord(text, maxLength) {
  if (!maxLength || text.length <= maxLength) return text
  const cut = text.slice(0, maxLength)
  const lastSpace = cut.lastIndexOf(' ')
  return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim()
}

// Artifacts of downloading the same file twice, which say nothing about the
// document. Conservative on purpose — anything else in the name is the author's.
const COPY_OF_PREFIX = /^copy of\s+/i
const DOWNLOAD_ARTIFACTS = [/\s*\(\d+\)$/, /\s+copy$/i]

/**
 * A source name from an uploaded file's name.
 *
 *   codebeamer-quick-start-guide.pdf  -> Codebeamer Quick Start Guide
 *   KEPServerEX_Manual_v6.11.pdf      -> KEPServerEX Manual v6.11
 *   release notes (1).pdf             -> Release Notes
 *   2024-03-15_release_notes.pdf      -> 2024-03-15 Release Notes
 *
 * Only the final extension is dropped, so `report.final.v2.pdf` keeps its
 * `.final.v2`. A name that humanizes to nothing falls back to the raw filename
 * rather than leaving the field empty.
 *
 * @param {string} [filename]
 * @param {number} [maxLength] — the Name field's character cap
 * @returns {string} — '' when there is nothing to derive from
 */
export function nameFromFilename(filename, maxLength) {
  if (!filename) return ''
  let base = filename.replace(/\.[^.]+$/, '').replace(COPY_OF_PREFIX, '')
  DOWNLOAD_ARTIFACTS.forEach(artifact => { base = base.replace(artifact, '') })
  return truncateAtWord(humanize(base) || filename, maxLength)
}

const WEB_PAGE_EXTENSION = /\.(html?|php|aspx?|jsp)$/i

// Path segments that name the routing rather than the page.
const UNINFORMATIVE_SEGMENTS = new Set(['index', 'default', 'home'])

// A segment that identifies rather than describes: a numeric id, or a long hex
// digest. Worth walking past — `/docs/1234` is better named "Docs" than "1234".
function isIdLike(segment) {
  return /^\d+$/.test(segment) || /^[0-9a-f]{8,}$/i.test(segment)
}

function decodeSegment(segment) {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment                                            // malformed escape; take it literally
  }
}

/**
 * Parse what the user typed, allowing for a missing scheme (`docs.example.com/x`
 * is what people paste). Returns null when it still won't parse — a half-typed
 * URL should leave the Name field alone rather than be guessed at.
 */
function parseUrl(url) {
  const trimmed = (url || '').trim()
  if (!trimmed) return null
  try {
    return new URL(trimmed)                                   // an explicit scheme is taken at its word
  } catch {
    // No scheme, or not a URL at all — try supplying one below.
  }
  try {
    const guessed = new URL(`https://${trimmed}`)
    // Supplying the scheme makes nearly any fragment parse: `htt` becomes the
    // host `htt`. A dotless host is far likelier to be half-typed than a real
    // intranet name, so this path accepts only a dotted one. Someone who types
    // the scheme themselves is trusted above, intranet host and all.
    return guessed.hostname.includes('.') ? guessed : null
  } catch {
    return null
  }
}

/**
 * A source name from the URL being indexed — the last path segment that
 * describes the page.
 *
 *   https://docs.example.com/kepware/edge/quick-start -> Quick Start
 *   https://docs.ptc.com/en/thingworx/install.html    -> Install
 *   https://en.wikipedia.org/wiki/Model-based_design  -> Model Based Design
 *   https://example.com/docs/1234?x=1#top             -> Docs
 *   https://example.com/                              -> example.com
 *
 * Query and hash are dropped; id-like and routing-only segments are walked past.
 * With no usable path the hostname stands in, left alone rather than humanized —
 * `example.com` reads as the placeholder it is, where "Example Com" would read
 * like a real answer.
 *
 * Names are unique per tenant, so two sites that both publish `/getting-started`
 * collide on the second add. That is left to the 409: short names read better,
 * and suffixing every suggestion with its host to prevent an occasional
 * collision is a poor trade.
 *
 * @param {string} [url]
 * @param {number} [maxLength] — the Name field's character cap
 * @returns {string} — '' when the URL can't be parsed
 */
export function nameFromUrl(url, maxLength) {
  const parsed = parseUrl(url)
  if (!parsed) return ''

  const segments = parsed.pathname.split('/').map(decodeSegment).filter(Boolean)
  while (segments.length) {
    const segment = segments.pop().replace(WEB_PAGE_EXTENSION, '')
    if (!segment || isIdLike(segment) || UNINFORMATIVE_SEGMENTS.has(segment.toLowerCase())) {
      continue
    }
    const name = humanize(segment)
    if (name) return truncateAtWord(name, maxLength)
  }
  return truncateAtWord(parsed.hostname.replace(/^www\./i, ''), maxLength)
}
